import { ConsoleLogger } from '@proposify/frontend-observability';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSections } from '../grid/reduxStore/editorSlice';
import { getMostVisibleElement } from './getMostVisibleElement';

const SECTION_WRAPPER = '.lazy-loaded-section';
const SECTION_ID_REGEX = /lazy-loaded-section-(\S*)/;
type SectionIndex = string;

export default function useActiveSection() {
  const s = useSelector(selectSections);
  const [activeSection, setActiveSection] = useState<string | undefined>();
  const [sections, setSections] = useState<Element[]>([]);
  const visibilityMap = useRef<Record<SectionIndex, number>>({});

  useEffect(() => {
    let attempts = 0;
    let delay = 1000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const verifyIfPageIsReady = () => {
      const detectedSections = Array.from(document.querySelectorAll(SECTION_WRAPPER));
      if (detectedSections.length > 0) {
        setSections(
          detectedSections.map((section) => {
            section.id = section.className.match(SECTION_ID_REGEX)?.[1] ?? '';
            return section;
          })
        );
        if (!activeSection) setActiveSection(detectedSections[0].id);
      } else if (attempts < 10) {
        attempts += 1;
        delay *= 1.5;
        timeoutId = setTimeout(verifyIfPageIsReady, delay);
      } else {
        ConsoleLogger.error(`editor.sections_drawer.detect_sections: no sections found after ${attempts} attempts`);
      }
    };

    verifyIfPageIsReady();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [s]);

  useEffect(() => {
    ConsoleLogger.log('Setting up observer for active section');
    const observer = new IntersectionObserver(
      (entry) => {
        entry.forEach(({ target, intersectionRatio: newVisibility }: IntersectionObserverEntry) => {
          // Verify if that section is scrolled in (visibility increases)
          const previousVisibility = visibilityMap.current[target.id];
          const variation = newVisibility - previousVisibility;
          if (previousVisibility && variation > 0) {
            // Only set active section if this section has highest visibility
            const maxVisibility = Math.max(...Object.values(visibilityMap.current));
            if (newVisibility >= maxVisibility) {
              setActiveSection(target.id);
              visibilityMap.current = {};
            }
          }
          visibilityMap.current[target.id] = newVisibility;
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      }
    );

    sections.forEach((target) => observer.observe(target));

    return () => {
      ConsoleLogger.log('Disconnecting observer for active section');
      observer.disconnect();
    };
  }, [sections]);

  useEffect(() => {
    const detected = Array.from(document.querySelectorAll(SECTION_WRAPPER));
    const mostVisibleSection = getMostVisibleElement(detected);
    if (mostVisibleSection) {
      ConsoleLogger.log(`Most visible section is ${mostVisibleSection.id}`);
      setActiveSection(mostVisibleSection.id);
      visibilityMap.current = {};
    }
  }, [s]);

  return { activeSection };
}
